import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Menu, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import es from "../../locales/es/flag.svg";
import en from "../../locales/en/flag.svg";
import pt_br from "../../locales/pt_br/flag.svg";
import { ArrowDown2, Global, TickCircle } from "iconsax-react";

const LANGUAGES = [
  { value: "pt-BR", label: "global.language.pt_br", flag: pt_br },
  { value: "en-US", label: "global.language.en_us", flag: en },
  { value: "es", label: "global.language.es", flag: es },
];

export default function LanguageSelect() {
  const theme = useMantineTheme();
  const { i18n, t } = useTranslation();
  const [value, setValue] = useState(i18n.language);
  const [opened, { toggle }] = useDisclosure(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    i18n.changeLanguage(value);
    localStorage.setItem("i18nextLng", value);
    queryClient.invalidateQueries();
  }, [value, i18n, queryClient]);

  const handleSelectProfile = (language: string) => {
    setValue(language);
    toggle();
  };
  return (
    <Menu
      shadow="md"
      width={280}
      zIndex={opened ? 300 : "-1"}
      offset={16}
      position="bottom-end"
      onOpen={toggle}
      onClose={toggle}
      closeOnItemClick={false}
      opened={opened}
      transitionProps={{
        transition: "scale-y",
        duration: 300,
        keepMounted: opened,
      }}
      styles={{
        dropdown: {
          width: 280,
          bg: theme.colors.other[0],
          borderRadius: "8px",
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.25)",
          padding: "12px !important",
          gap: "8px !important",
        },
        item: {
          transition: "0.5s",
          padding: "8px",
          margin: "4px 0",
        },
        itemIcon: {
          marginRight: "16px",
        },
      }}
    >
      <Menu.Target>
        <Flex
          h={{ base: 48, 991: 56 }}
          align="center"
          justify="center"
          style={{
            position: "relative",
            content: "''",
            zIndex: "200",
          }}
        >
          <Flex
            h={{ base: 48, 991: 56 }}
            p={"5px 12px"}
            bg={theme.colors.other[0]}
            gap={8}
            direction="column"
            align="center"
            justify="center"
            style={{
              overflow: "hidden",
              position: "absolute",
              top: 0,
              right: 0,
              borderRadius: "8px",
              transition: "all 0.3s linear",
            }}
          >
            <Flex
              gap={"4px"}
              align="center"
              justify="space-between"
              w="fit-content"
              style={{
                transition: "width 0.3s linear, height 0.3s linear",
              }}
            >
              <Global size={28} color={theme.colors.basic[5]} />

              <ArrowDown2
                size={28}
                color={theme.colors.basic[5]}
                style={{
                  transform: opened ? "rotatex(180deg)" : "unset",
                  transition: "0.5s",
                }}
              />
            </Flex>
          </Flex>
        </Flex>
      </Menu.Target>
      <Menu.Dropdown>
        {LANGUAGES.map((role, index) => {
          const isRoleSelected = role.value === i18n.language;
          return (
            <Menu.Item
              key={`role${index}`}
              onClick={() => handleSelectProfile(role.value)}
              icon={
                <Flex
                  h="28px"
                  w="28px"
                  align="center"
                  justify="center"
                  style={{ borderRadius: "8px" }}
                >
                  <img
                    src={role.flag}
                    alt={t(role.label)}
                    width={22}
                    height={22}
                  />
                </Flex>
              }
              bg={isRoleSelected ? theme.colors.primary[0] : "unset"}
              color={isRoleSelected ? "white" : "unset"}
              sx={(theme) => ({
                "&:hover": {
                  backgroundColor: isRoleSelected
                    ? theme.colors.primary[1]
                    : theme.colors.primaryOpacity[4],
                  color: isRoleSelected ? "white" : theme.colors.basic[6],
                },
              })}
            >
              <Flex w="100%" justify="space-between" align={"center"}>
                {t(role.label)}
                {isRoleSelected ? <TickCircle size="18" color="white" /> : null}
              </Flex>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
